import * as Sentry from '@sentry/react';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Snackbar, SnackbarContent } from '@mui/material';

import { CopyAllRounded } from '@mui/icons-material';

import SentryError from '../../../assets/sentry-fallback.svg?react';

import React, { useState } from 'react';
import { ButtonUi } from '../../UI/FallbackButton/ButtonUi';
import { AppImage } from './AppImage';
import { sentryFallbackStyle } from './style';

interface SentryFallbackProps {
  error: unknown;
  eventId: string;
  onRetry: () => void;
  title: string;
  subtitle: string;
}

export const SentryFallback = (props: SentryFallbackProps) => {
  const { eventId, title = 'sentry.fallback.title', subtitle = 'sentry.fallback.subtitle' } = props;
  const { sentryFallback } = sentryFallbackStyle;
  const { t } = useTranslation();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const report = () => {
    Sentry.showReportDialog({ eventId });
  };

  const copyId = async () => {
    const id = String(eventId ?? '');
    if (!id) return;

    try {
      await navigator.clipboard.writeText(id);
    } catch {
      // Fallback (may show as deprecated in editor; that's okay)
      const ta = document.createElement('textarea');
      ta.value = id;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }

    setSnackMessage(t('sentry.fallback.reference-copied'));
    setSnackOpen(true);
  };

  return (
    <Box sx={sentryFallback} className="sf-page">
      <AppImage className="logo" src="/assets/fallBack/psylaris-logo.jpg" />
      <Box className="sf-card" role="alert" aria-live="assertive">
        <SentryError className="error-icon" />
        <Typography variant="h1" className="sf-title">
          {t(title)}
        </Typography>
        <Typography variant="h4" className="sf-subtitle">
          {t(subtitle)}
        </Typography>

        {!!eventId && (
          <Typography variant="h6" className="sf-ref">
            {t('sentry.fallback.reference')}: <code>#{eventId.slice(0, 8)}</code>
            <ButtonUi
              isIconBtn
              className="sf-link"
              size="small"
              tooltipTitle={t('buttons.click-to-copy')}
              onClickHandler={copyId}
            >
              <CopyAllRounded />
            </ButtonUi>
          </Typography>
        )}
        <Box className="sf-actions">
          <ButtonUi
            className="sf-reload"
            variant="contained"
            type="primary"
            size="small"
            onClickHandler={() => window.location.reload()}
          >
            {t('buttons.reload-page')}
          </ButtonUi>
          <ButtonUi className="sf-report" variant="outlined" type="secondary" size="small" onClickHandler={report}>
            {t('buttons.report-issue')}
          </ButtonUi>
        </Box>
        <Snackbar
          open={snackOpen}
          autoHideDuration={1600}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setSnackOpen(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <SnackbarContent className="sf-snackbar" message={snackMessage} />
        </Snackbar>
      </Box>
    </Box>
  );
};
